import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Eye, LogOut, Settings, Search, GripVertical, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Row Component
function SortableProjectRow({ project, categories, deleteProject, onEdit, isSelected, onSelect }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: project.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`border-b border-[hsl(var(--bk-line))] ${isDragging ? 'bg-[hsl(var(--bk-bg))]' : ''} ${isSelected ? 'bg-[hsl(var(--bk-accent))]/10' : ''}`}
      data-testid={`project-row-${project.slug}`}
    >
      <td className="p-4">
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onSelect(project.id)}
          data-testid={`checkbox-${project.id}`}
        />
      </td>
      <td className="p-4 cursor-grab active:cursor-grabbing" {...attributes} {...listeners}>
        <GripVertical size={16} className="text-[hsl(var(--bk-fg-muted))]" />
      </td>
      <td className="p-4 font-body text-sm text-[hsl(var(--bk-fg))]">{project.title}</td>
      <td className="p-4 font-mono-meta text-xs text-[hsl(var(--bk-fg-muted))]">
        {categories.find(c => c.id === project.category_id)?.name || 'Uncategorized'}
      </td>
      <td className="p-4 font-body text-sm text-[hsl(var(--bk-fg-muted))]">{project.year}</td>
      <td className="p-4">
        <Badge variant={project.status === 'published' ? 'default' : 'secondary'} className={project.status === 'published' ? 'bg-green-600' : 'bg-yellow-600'}>
          {project.status}
        </Badge>
      </td>
      <td className="p-4 font-mono-meta text-xs text-[hsl(var(--bk-fg-muted))]">
        {new Date(project.updated_at || project.created_at).toLocaleDateString()}
      </td>
      <td className="p-4">
        <div className="flex items-center justify-end gap-2">
          {project.status === 'published' ? (
            <Link to={`/work/${project.slug}`} target="_blank">
              <Button variant="ghost" size="sm" title="View Published"><Eye size={14} /></Button>
            </Link>
          ) : (
            <Link to={`/work/${project.slug}?preview=true`} target="_blank">
              <Button variant="ghost" size="sm" title="Preview Draft" className="text-yellow-600"><Eye size={14} /></Button>
            </Link>
          )}
          <Link to={`/admin/projects/edit/${project.id}`}>
            <Button variant="ghost" size="sm" onClick={onEdit}><Edit size={14} /></Button>
          </Link>
          <Button variant="ghost" size="sm" onClick={() => deleteProject(project.id)}>
            <Trash2 size={14} className="text-red-500" />
          </Button>
        </div>
      </td>
    </tr>
  );
}

export default function AdminProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const navigate = useNavigate();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    const isAuth = sessionStorage.getItem('admin_auth');
    if (!isAuth) {
      navigate('/admin');
      return;
    }
    loadData();
  }, [navigate]);

  useEffect(() => {
    // Filter projects based on search query
    if (searchQuery.trim() === '') {
      setFilteredProjects(projects);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = projects.filter(project =>
        project.title.toLowerCase().includes(query) ||
        project.year.includes(query) ||
        (categories.find(c => c.id === project.category_id)?.name || '').toLowerCase().includes(query)
      );
      setFilteredProjects(filtered);
    }
  }, [searchQuery, projects, categories]);

  const loadData = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const [projectsRes, categoriesRes] = await Promise.all([
        fetch(`${backendUrl}/api/admin/projects`),
        fetch(`${backendUrl}/api/admin/project-categories`)
      ]);
      const projectsData = await projectsRes.json();
      setProjects(projectsData);
      setFilteredProjects(projectsData);
      setCategories(await categoriesRes.json());
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const deleteProject = async (id) => {
    if (!window.confirm('Delete this project? This cannot be undone.')) return;
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${backendUrl}/api/admin/projects/${id}`, { method: 'DELETE' });
      toast.success('Project deleted');
      loadData();
      setSelectedProjects(prev => prev.filter(pid => pid !== id));
    } catch (err) {
      toast.error('Failed to delete project');
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = filteredProjects.findIndex(p => p.id === active.id);
      const newIndex = filteredProjects.findIndex(p => p.id === over.id);

      const newOrder = arrayMove(filteredProjects, oldIndex, newIndex);
      setFilteredProjects(newOrder);
      setProjects(newOrder);

      // Save new order to backend
      await saveProjectOrder(newOrder);
    }
  };

  const saveProjectOrder = async (orderedProjects) => {
    setIsSaving(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const updates = orderedProjects.map((project, index) => ({
        id: project.id,
        order: index
      }));

      await fetch(`${backendUrl}/api/admin/projects/reorder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });

      toast.success('Project order saved');
    } catch (err) {
      toast.error('Failed to save project order');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedProjects(filteredProjects.map(p => p.id));
    } else {
      setSelectedProjects([]);
    }
  };

  const handleSelectProject = (id) => {
    setSelectedProjects(prev =>
      prev.includes(id) ? prev.filter(pid => pid !== id) : [...prev, id]
    );
  };

  const handleBulkPublish = async () => {
    if (selectedProjects.length === 0) {
      toast.error('No projects selected');
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${backendUrl}/api/admin/projects/bulk-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_ids: selectedProjects,
          status: 'published'
        })
      });

      toast.success(`${selectedProjects.length} project(s) published`);
      setSelectedProjects([]);
      loadData();
    } catch (err) {
      toast.error('Failed to publish projects');
    }
  };

  const handleBulkUnpublish = async () => {
    if (selectedProjects.length === 0) {
      toast.error('No projects selected');
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${backendUrl}/api/admin/projects/bulk-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_ids: selectedProjects,
          status: 'draft'
        })
      });

      toast.success(`${selectedProjects.length} project(s) unpublished`);
      setSelectedProjects([]);
      loadData();
    } catch (err) {
      toast.error('Failed to unpublish projects');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedProjects.length === 0) {
      toast.error('No projects selected');
      return;
    }

    if (!window.confirm(`Delete ${selectedProjects.length} project(s)? This cannot be undone.`)) return;

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${backendUrl}/api/admin/projects/bulk-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_ids: selectedProjects })
      });

      toast.success(`${selectedProjects.length} project(s) deleted`);
      setSelectedProjects([]);
      loadData();
    } catch (err) {
      toast.error('Failed to delete projects');
    }
  };

  const logout = () => {
    sessionStorage.removeItem('admin_auth');
    navigate('/admin');
  };

  if (loading) return <div className="min-h-screen bg-[hsl(var(--bk-bg))] flex items-center justify-center"><p className="text-[hsl(var(--bk-fg))]">Loading...</p></div>;

  return (
    <div className="min-h-screen bg-[hsl(var(--bk-bg))] p-8" data-testid="admin-projects-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl text-[hsl(var(--bk-fg))] mb-2">Projects Management</h1>
            <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))]">BAKLIGHT Admin</p>
          </div>
          <div className="flex gap-4">
            <Link to="/admin">
              <Button variant="outline" className="border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]">← Back to Admin</Button>
            </Link>
            <Link to="/admin/projects/settings">
              <Button variant="outline" className="border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"><Settings size={16} className="mr-2" /> Settings</Button>
            </Link>
            <Link to="/admin/projects/new">
              <Button className="bg-[hsl(var(--bk-accent))] text-[hsl(var(--bk-bg))]">
                <Plus size={16} className="mr-2" /> New Project
              </Button>
            </Link>
            <Button variant="outline" onClick={logout} className="border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"><LogOut size={16} className="mr-2" /> Logout</Button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--bk-fg-muted))]" size={18} />
            <Input
              type="text"
              placeholder="Search by title, category, or year..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-[hsl(var(--bk-bg-elev))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
              data-testid="search-input"
            />
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {selectedProjects.length > 0 && (
          <div className="mb-6 p-4 bg-[hsl(var(--bk-accent))]/10 border border-[hsl(var(--bk-accent))]/30 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-4">
              <p className="font-body text-sm text-[hsl(var(--bk-fg))]">
                {selectedProjects.length} project(s) selected
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSelectedProjects([])}
                className="border-[hsl(var(--bk-line))]"
              >
                <X size={14} className="mr-1" /> Clear
              </Button>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleBulkPublish}
                className="bg-green-600 hover:bg-green-700"
                data-testid="bulk-publish-btn"
              >
                <Check size={14} className="mr-1" /> Publish Selected
              </Button>
              <Button
                size="sm"
                onClick={handleBulkUnpublish}
                className="bg-yellow-600 hover:bg-yellow-700"
                data-testid="bulk-unpublish-btn"
              >
                Unpublish Selected
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={handleBulkDelete}
                data-testid="bulk-delete-btn"
              >
                <Trash2 size={14} className="mr-1" /> Delete Selected
              </Button>
            </div>
          </div>
        )}

        <div className="bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))] rounded-lg overflow-hidden">
          {isSaving && (
            <div className="p-2 bg-blue-600/20 border-b border-blue-600/30 text-center">
              <p className="text-xs text-blue-400">Saving order...</p>
            </div>
          )}
          <table className="w-full">
            <thead className="border-b border-[hsl(var(--bk-line))]">
              <tr>
                <th className="p-4 text-left">
                  <Checkbox
                    checked={selectedProjects.length === filteredProjects.length && filteredProjects.length > 0}
                    onCheckedChange={handleSelectAll}
                    data-testid="select-all-checkbox"
                  />
                </th>
                <th className="p-4 font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))]"></th>
                <th className="text-left p-4 font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))]">TITLE</th>
                <th className="text-left p-4 font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))]">CATEGORY</th>
                <th className="text-left p-4 font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))]">YEAR</th>
                <th className="text-left p-4 font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))]">STATUS</th>
                <th className="text-left p-4 font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))]">UPDATED</th>
                <th className="text-right p-4 font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))]">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filteredProjects.length === 0 ? (
                <tr>
                  <td colSpan="8" className="p-8 text-center font-body text-sm text-[hsl(var(--bk-fg-muted))]">
                    {searchQuery ? 'No projects match your search.' : 'No projects yet. Create your first project!'}
                  </td>
                </tr>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={filteredProjects.map(p => p.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {filteredProjects.map(project => (
                      <SortableProjectRow
                        key={project.id}
                        project={project}
                        categories={categories}
                        deleteProject={deleteProject}
                        isSelected={selectedProjects.includes(project.id)}
                        onSelect={handleSelectProject}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
